// B2B Customer Import — CSV upload
import { useState, useCallback, useRef } from "react";
import {
  Upload,
  FileText,
  Download,
  CheckCircle2,
  AlertCircle,
  XCircle,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { useB2BImport } from "@/hooks/admin/useB2B";
import type { B2BImportResult } from "@/lib/admin-api/b2b";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CSV_COLUMNS = [
  { key: "company_name", label: "company_name", description: "Nom de l'entreprise (requis)" },
  { key: "contact_name", label: "contact_name", description: "Nom du contact (requis)" },
  { key: "email", label: "email", description: "Adresse email (requis)" },
  { key: "phone", label: "phone", description: "Téléphone au format +221… (requis)" },
  { key: "region", label: "region", description: "Région sénégalaise (requis)" },
  { key: "credit_limit_fcfa", label: "credit_limit_fcfa", description: "Limite de crédit en FCFA (requis, nombre entier)" },
  { key: "payment_terms_days", label: "payment_terms_days", description: "Termes de paiement en jours (requis, nombre entier)" },
];

const SAMPLE_CSV_CONTENT = [
  CSV_COLUMNS.map((c) => c.key).join(","),
  "Entreprise ABC,Mamadou Diallo,m.diallo@abc.sn,+221771234567,Dakar,500000,30",
  "Société XYZ,Aminata Fall,a.fall@xyz.sn,+221782345678,Kaolack,1000000,60",
].join("\n");

// ---------------------------------------------------------------------------
// Results table
// ---------------------------------------------------------------------------

interface ImportResultsProps {
  readonly result: B2BImportResult;
}

function ImportResults({ result }: ImportResultsProps) {
  return (
    <div className="space-y-4 rounded-lg border border-stone-200 bg-white p-5">
      <h3 className="font-medium text-stone-900">Résultats de l'import</h3>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-3">
        <div className="rounded-md bg-stone-50 p-3 text-center">
          <p className="text-xl font-semibold text-stone-900">
            {result.total_rows}
          </p>
          <p className="text-xs text-stone-500">Lignes totales</p>
        </div>
        <div className="rounded-md bg-emerald-50 p-3 text-center">
          <p className="text-xl font-semibold text-emerald-700">
            {result.imported}
          </p>
          <p className="text-xs text-emerald-600">Importées</p>
        </div>
        <div className="rounded-md bg-amber-50 p-3 text-center">
          <p className="text-xl font-semibold text-amber-700">
            {result.skipped}
          </p>
          <p className="text-xs text-amber-600">Ignorées</p>
        </div>
        <div className="rounded-md bg-red-50 p-3 text-center">
          <p className="text-xl font-semibold text-red-700">
            {result.errors.length}
          </p>
          <p className="text-xs text-red-600">Erreurs</p>
        </div>
      </div>

      {/* Errors table */}
      {result.errors.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-red-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-red-100 bg-red-50">
                <th className="px-4 py-2 text-left text-xs font-medium text-red-600">
                  Ligne
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-red-600">
                  Erreur
                </th>
              </tr>
            </thead>
            <tbody>
              {result.errors.map((e, i) => (
                <tr key={i} className="border-b border-red-50">
                  <td className="px-4 py-2 font-mono text-xs text-stone-600">
                    {e.row}
                  </td>
                  <td className="px-4 py-2 text-xs text-red-700">
                    {e.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Import() {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<B2BImportResult | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importMutation = useB2BImport();

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0] ?? null;
      setFile(f);
      setResult(null);
      setApiError(null);
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!file) return;
    setApiError(null);
    setResult(null);
    try {
      const res = await importMutation.mutateAsync(file);
      setResult(res);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setApiError(
        err instanceof Error ? err.message : "Une erreur est survenue.",
      );
    }
  }, [file, importMutation]);

  const handleDownloadSample = useCallback(() => {
    const blob = new Blob([`\uFEFF${SAMPLE_CSV_CONTENT}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modele-import-b2b.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <AdminLayout>
      <div className="mx-auto max-w-2xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Upload className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Import clients B2B
            </h1>
            <p className="text-sm text-stone-500">
              Importez plusieurs clients B2B depuis un fichier CSV
            </p>
          </div>
        </div>

        {/* Format explanation */}
        <div className="rounded-lg border border-stone-200 bg-white p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-stone-400" />
              <h2 className="text-sm font-semibold text-stone-900">
                Format attendu (CSV)
              </h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadSample}
              className="gap-1.5"
            >
              <Download className="h-3.5 w-3.5" />
              Télécharger le modèle
            </Button>
          </div>

          <div className="overflow-x-auto rounded-md border border-stone-100 bg-stone-50">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="px-4 py-2 text-left text-xs font-medium text-stone-500">
                    Colonne
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-stone-500">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {CSV_COLUMNS.map((col) => (
                  <tr key={col.key} className="border-b border-stone-100">
                    <td className="px-4 py-2 font-mono text-xs text-amber-700">
                      {col.label}
                    </td>
                    <td className="px-4 py-2 text-xs text-stone-600">
                      {col.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="space-y-1 text-xs text-stone-500">
            <li className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
              La première ligne doit contenir les en-têtes de colonnes
            </li>
            <li className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
              Encodage UTF-8 recommandé
            </li>
            <li className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
              Séparateur : virgule (,)
            </li>
          </ul>
        </div>

        {/* Upload zone */}
        <div className="rounded-lg border border-stone-200 bg-white p-5 space-y-4">
          <h2 className="text-sm font-semibold text-stone-900">
            Importer un fichier
          </h2>

          <div
            className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-stone-200 bg-stone-50 px-6 py-10 cursor-pointer hover:border-amber-300 hover:bg-amber-50/30 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-8 w-8 text-stone-400" />
            <div className="text-center">
              <p className="text-sm font-medium text-stone-700">
                {file ? file.name : "Cliquez pour sélectionner un fichier CSV"}
              </p>
              <p className="text-xs text-stone-400">
                {file
                  ? `${(file.size / 1024).toFixed(1)} Ko`
                  : "Format CSV uniquement"}
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Error */}
          {apiError && (
            <div className="flex items-center gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <XCircle className="h-4 w-4 shrink-0" />
              <span>{apiError}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            {file && (
              <Button
                variant="outline"
                onClick={() => {
                  setFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              >
                Annuler
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              disabled={!file || importMutation.isPending}
              className="gap-1.5"
            >
              {importMutation.isPending ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {importMutation.isPending ? "Import en cours…" : "Lancer l'import"}
            </Button>
          </div>
        </div>

        {/* Results */}
        {result && <ImportResults result={result} />}
      </div>
    </AdminLayout>
  );
}
